import { useStore } from '@nanostores/solid'
import { useIsMutating } from '@tanstack/solid-query'
import { createResource, Match, Show, Switch } from 'solid-js'
import { useChinaAndroidTrialMutation } from '~/hooks/use-china-android-trial-mutation'
import { useSubscribeMutation } from '~/hooks/use-subscribe-mutation'
import { productsReady } from '~/lib/message'
import { platform } from '~/lib/params'
import { selectPlanVariant } from '~/lib/select-plan-variant'
import { m } from '~/paraglide/messages'
import {
  $chinaAndroidTrialEligibility,
  $isReminderViewOpen,
  $selectedPlan,
} from '~/store'

export function FixedSubscribeButton() {
  const [products] = createResource(() => productsReady.promise)
  const chinaTrial = useStore($chinaAndroidTrialEligibility)
  const selectedPlan = useStore($selectedPlan)

  const variant = () =>
    selectPlanVariant({
      platform,
      products: products() ?? null,
      chinaAndroidTrialEligibility: chinaTrial(),
    })

  const subscribeMutation = useSubscribeMutation()
  const chinaAndroidTrialMutation = useChinaAndroidTrialMutation()
  const isMutating = useIsMutating()

  const selectedProduct = () => {
    const v = variant()
    if (v.kind !== 'two-plans') return undefined
    const key = selectedPlan() === 'annual' ? 'annualEarlyBird' : v.right
    return products()?.[key]
  }

  const handleClick = () => {
    const v = variant()
    if (v.kind === 'loading' || v.kind === 'error') return
    if (v.kind === 'china-trial-cta') {
      chinaAndroidTrialMutation.mutate()
      return
    }
    const product = selectedProduct()
    if (!product) return
    // Trial path: open the reminder view first; user confirms there before
    // the IAP fires. No-trial path: subscribe immediately.
    if ((product.eligible_trial_days ?? 0) > 0) {
      $isReminderViewOpen.set(true)
      return
    }
    subscribeMutation.mutate({ product })
  }

  const label = () => {
    const v = variant()
    if (v.kind === 'china-trial-cta')
      return m.btn_start_free_trial_day({ free_trial_day: 7 })
    if (platform !== 'china_android') {
      const days = selectedProduct()?.eligible_trial_days ?? 0
      if (days > 0) return m.btn_start_free_trial_day({ free_trial_day: days })
    }
    return m.btn_unlock_plus()
  }

  const description = (): string | undefined => {
    const v = variant()
    if (v.kind === 'china-trial-cta') {
      return m.onboarding_paywall_footer_trial_cna()
    }
    if (platform === 'china_android') {
      return m.onboarding_paywall_footer_cna()
    }
    const days = selectedProduct()?.eligible_trial_days ?? 0
    if (days > 0) return m.subscription_walkthrough_p1_trial_fee()
    return undefined
  }

  const disabled = () =>
    variant().kind === 'loading' ||
    variant().kind === 'error' ||
    isMutating() > 0

  return (
    <div
      class="fixed bottom-0 left-0 right-0 flex flex-col items-center gap-3 bg-[#060e08] px-6 pb-9 pt-3"
      style={{ 'box-shadow': '0 -3px 3px 0 rgba(255,255,255,0.2)' }}
    >
      <div class="flex w-full max-w-[640px] flex-col items-center gap-3">
        <Show when={description()}>
          <p class="text-center text-xs text-white/60">{description()}</p>
        </Show>
        <button
          type="button"
          class="flex h-[52px] w-full max-w-[640px] cursor-pointer items-center justify-center rounded-xl border border-[#c5ff70] px-[52px] text-center text-base font-bold text-[#054442] disabled:opacity-60"
          style={{
            'background-image':
              'linear-gradient(106deg, #c5ff70 13%, #30d3ff 97%)',
          }}
          onClick={handleClick}
          disabled={disabled()}
        >
          {label()}
        </button>
        <Switch>
          <Match when={variant().kind === 'two-plans'}>
            <button
              type="button"
              class="h-10 text-center text-base font-bold text-white/60"
              onClick={() => {
                // TODO(pass-event-paywall): open legal details sheet — port new-user-paywall BottomLinks in a follow-up
              }}
            >
              {m.subscription_view_complete_benefit()}
            </button>
          </Match>
        </Switch>
      </div>
    </div>
  )
}
